import React, { useEffect, useState } from 'react';
import type { ColumnMapping, CurrencyEnum, FileMapping } from 'venn-api';
import {
  determineFormat,
  formatOptions,
  type ColumnMappingMetadata,
  type FormatType,
  type ReviewCellRendererProps,
  parseFormat,
} from './helpers';
import { CheckboxMenu, DropMenu, type DropMenuCheckboxItem, type DropMenuItem, Label } from 'venn-ui-kit';
import { convertToTriggerableMenuOption } from './renderers/rendererHelpers';
import { cn } from '@/lib/utils';
import type { AppendType } from '../../types';
import { useHasFF } from 'venn-utils';

interface BulkInvestmentEditorProps {
  bulkEditableProps: BulkEditableProps;
  cellRendererParams: ReviewCellRendererProps;
  selectedRows: ColumnMapping[];
  onBulkChange: (columns: ColumnMapping[], values: ColumnMappingMetadata) => void;
  onBulkOwnershipChange: (columns: ColumnMapping[], updatedOwnerships: DropMenuCheckboxItem[]) => FileMapping;
}

export interface BulkEditableProps {
  selectedCurrencies: Set<string>;
  selectedFormats: Set<FormatType>;
  selectedOwnerships: DropMenuCheckboxItem[];
}

export const BulkInvestmentEditor = ({
  cellRendererParams,
  bulkEditableProps,
  selectedRows,
  onBulkChange,
  onBulkOwnershipChange,
}: BulkInvestmentEditorProps) => {
  const { metadata, onMappingChange } = cellRendererParams;

  const [uniqueSelectedCurrencies, setUniqueSelectedCurrencies] = useState(new Set<string>());
  const [uniqueSelectedFormats, setUniqueSelectedFormats] = useState(new Set<FormatType>());
  useEffect(() => {
    setUniqueSelectedCurrencies(new Set(bulkEditableProps.selectedCurrencies));
    setUniqueSelectedFormats(new Set(bulkEditableProps.selectedFormats));
  }, [bulkEditableProps.selectedCurrencies, bulkEditableProps.selectedFormats, bulkEditableProps.selectedOwnerships]);
  const hasInvestmentOwnership = useHasFF('investment_ownership_ff');

  if (selectedRows.length < 1) {
    return null;
  }

  const menuComponent = (_highlighted: DropMenuItem | undefined, _forceCollapse: () => void, className?: string) => (
    <div
      className={cn([
        '[&_input:indeterminate]:before:bg-venn-dark-blue [&_input:indeterminate:hover]:before:bg-venn-deep-light-blue',
        className,
      ])}
    >
      <CheckboxMenu
        width={250}
        height={200}
        hideSelectAll
        items={bulkEditableProps.selectedOwnerships}
        onChange={(newOwnerships) => {
          const newMapping = onBulkOwnershipChange(selectedRows, newOwnerships);
          onMappingChange(newMapping);
        }}
        refreshedStyling
      />
    </div>
  );

  const allNewInvestmentsSelected =
    cellRendererParams &&
    selectedRows.every((row) => cellRendererParams.additionalMetadata?.perSeries?.[row.seriesId]?.isNew);

  const currencyOptions = metadata.currency.map(convertToTriggerableMenuOption);

  const disabledActionsOption = {
    value: 'disabled',
    label: 'Create new investment',
  };
  const mixedActionsOption = {
    value: 'mixed',
    label: 'Mixed',
  };
  const actionOptions = allNewInvestmentsSelected
    ? [disabledActionsOption]
    : metadata.appendType.map(convertToTriggerableMenuOption);

  const selectedActions = new Set<AppendType>();
  selectedRows.forEach((row) => {
    if (row.appendId) selectedActions.add(row.appendId);
  });
  const selectedAction =
    selectedActions.size === 1
      ? actionOptions.find((o) => o.value === selectedActions.values().next().value)
      : mixedActionsOption;

  return (
    <div className="bg-venn-grey-10 pt-[8px] pb-[24px] px-[24px] -mb-[24px] border-0 border-t border-solid border-venn-grey-30 w-full">
      <div className="text-[14px] leading-[14px] font-bold">Apply to all selected investments</div>
      <div className="mt-[8px] flex gap-[16px] justify-between">
        <div className="flex-1">
          <Label>Format</Label>
          <DropMenu
            items={formatOptions}
            className="w-full mt-[4px] [&_span>label]:font-normal"
            usePortal
            menuAbove
            selected={determineFormat(selectedRows[0]!)}
            getSelectionLabel={(opt) =>
              uniqueSelectedFormats.size && uniqueSelectedFormats.size === 1 ? opt.label : 'Mixed'
            }
            onChange={(selection) => {
              const val = parseFormat(selection.value as FormatType);
              onBulkChange(selectedRows, val);
            }}
          />
        </div>
        <div className="flex-1">
          <Label>Currency</Label>
          <DropMenu
            items={currencyOptions}
            className="w-full mt-[4px] [&_span>label]:font-normal"
            usePortal
            menuAbove
            selected={selectedRows[0]!.currency}
            getSelectionLabel={(opt) =>
              uniqueSelectedCurrencies.size && uniqueSelectedCurrencies.size === 1 ? opt.label : 'Mixed'
            }
            onChange={(selection) => onBulkChange(selectedRows, { currency: selection.value as CurrencyEnum })}
          />
        </div>
        {hasInvestmentOwnership && (
          <div className="flex-1">
            <Label>Ownership</Label>
            <DropMenu
              items={bulkEditableProps.selectedOwnerships}
              className="w-full mt-[4px] [&_span>label]:font-normal"
              usePortal
              menuAbove
              menuComponent={menuComponent}
              comparer={(a, b) => a === b}
              selected={bulkEditableProps.selectedOwnerships[0]?.value ?? ''}
              getSelectionLabel={() => {
                let indeterminateCount = 0;
                const selected: string[] = [];
                bulkEditableProps.selectedOwnerships.forEach((i) => {
                  if (i.checked) selected.push(i.label);
                  if (i.indeterminate) indeterminateCount++;
                });
                if (selected.length === 0 && indeterminateCount === 0) return 'Select ownership';
                if (selected.length === 1 && indeterminateCount === 0) {
                  return selected[0]!;
                }
                if (!indeterminateCount) return `${selected.length} selected`;
                return 'Mixed';
              }}
            />
          </div>
        )}
        <div className="flex-1">
          <Label>Action</Label>
          <DropMenu
            items={actionOptions}
            className="w-full mt-[4px] [&_span>label]:font-normal"
            usePortal
            menuAbove
            disabled={allNewInvestmentsSelected}
            headerComponent={
              <div className="border-0 border-b border-solid border-venn-grey-30 text-venn-grey-80 pt-[15px] pb-[7px] px-[9px] [&+div]:pt-0">
                For existing investments only
              </div>
            }
            selected={allNewInvestmentsSelected ? disabledActionsOption.value : (selectedAction?.value ?? '')}
            onChange={(selection) => onBulkChange(selectedRows, { appendId: selection.value as AppendType })}
          />
        </div>
      </div>
    </div>
  );
};
